import Big from 'big.js'
import {
  Address,
  PaymentResponse,
  PaymentStatusKey,
  PaymentStartRequestBodyForEnrollments,
  PaymentStartRequestBodyForTypeEnum,
  PaymentMethodKey,
  CashAmount,
} from '../../../swagger'
import {
  LcdAmountSelection,
  PaymentBillingAddressValidationSteps,
  PaymentModalSteps,
} from './paymentModalSteps'
import { extractedErrorObject, paymentsApi } from '../../../api/swagger'
import { startPayment, payByAch } from '../../../api/payments'
import {
  convertTwoDecimalStringToBig,
  formatBigFixedTwoDecimals,
} from '../../../utils/bigJSHelper'
import { getAmountFromLocaleCurrency } from '../../../utils/getAmountFromLocalCurrency'
import { LcdProgramSelection } from '../PaymentModal/types'

export interface HandleChoosePaymentMethodProps {
  isLicensing: boolean
  isBankSelected: boolean
  isLcd?: boolean
  total: Big
  totalAchDiscount: Big
  unpaidEnrollments?: PaymentStartRequestBodyForEnrollments[]
  programKey: number
  amountOfPayment: string
  actorKey?: number
  defaultErrorMessage: string
}

export interface HandleEnterAmountProps {
  programKey: number
  amountOfPayment: string
  total: Big
  actorKey?: number
  defaultErrorMessage: string
}

export interface HandleEnterBillingAddressProps {
  stepToValidatePaymentBillingAddress?: PaymentBillingAddressValidationSteps
  validateAddress: () => Promise<boolean>
  geocodeAddress: () => Promise<boolean>
  updateBillingPaymentAddress: (selectedAddress?: Address) => Promise<void>
  selectedValidAddress: string
  billingAddressInformation: {
    fullName: string
    address: Address
  }
  initialValueForPaymentBillingAddress: undefined
}

export interface HandleEnterPaymentInformationProps {
  bankPaymentInformation: {
    firstName: string
    lastName: string
    routingNumber: string
    bankAccountNumber: string
    confirmBankAccountNumber: string
  }
  bankAccountMatch: { input: boolean }
  validRoutingNumber: { input: boolean }
  paymentInformation?: PaymentResponse
  isBankSelected: boolean
  bankAccountsDoesNotMatch: string
  defaultMessageError: string
  defaultAchErrorMessage: string
  contactCustomerSupportMessage: string
}

export interface HandleLCDStepProps {
  selectedPrograms: number[]
  lcdAmountOption: LcdAmountSelection
  amountOfPayment: string
  defaultErrorMessage: string
}

export interface HandleLCDAmountStepProps {
  selectedPrograms: number[]
  lcdAmountOption: LcdAmountSelection
  amountOfPayment: string
  defaultErrorMessage: string
}

export interface HandlerResult {
  success: boolean
  shouldContinue: boolean
  error?: string
}

export const handleChoosePaymentMethod = async (
  props: HandleChoosePaymentMethodProps,
  setPaymentInformation: (payment: PaymentResponse) => void,
  setStep: (step: PaymentModalSteps) => void,
  setErrorMessage: (msg: string) => void,
  setSubmissionInProgress: (inProgress: boolean) => void
): Promise<void> => {
  const {
    isLicensing,
    isBankSelected,
    isLcd,
    total,
    totalAchDiscount,
    unpaidEnrollments,
    defaultErrorMessage,
  } = props

  if (!isLicensing) {
    try {
      const resultingPayment = await startPayment({
        body: {
          _for: {
            type: PaymentStartRequestBodyForTypeEnum.Enrollment,
            enrollments: unpaidEnrollments ?? [],
          },
          amount: formatBigFixedTwoDecimals(
            isBankSelected ? total.minus(totalAchDiscount) : total
          ),
          method: isBankSelected ? PaymentMethodKey.Ach : PaymentMethodKey.Cc,
        },
      })
      setPaymentInformation(resultingPayment)
      setStep(PaymentModalSteps.EnterBillingAddress)
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown',
        message: (e as unknown as Error).message ?? defaultErrorMessage,
      }
      setErrorMessage(errorObject.message)
    } finally {
      setSubmissionInProgress(false)
    }
  } else {
    if (isLcd) {
      setStep(PaymentModalSteps.LCDStep)
    } else {
      setStep(PaymentModalSteps.EnterAmount)
    }
    setSubmissionInProgress(false)
  }
}

export const handleEnterAmount = async (
  props: HandleEnterAmountProps,
  setPaymentInformation: (payment: PaymentResponse) => void,
  setStep: (step: PaymentModalSteps) => void,
  setErrorMessage: (msg: string) => void,
  setSubmissionInProgress: (inProgress: boolean) => void
): Promise<void> => {
  const { programKey, amountOfPayment, total, actorKey, defaultErrorMessage } =
    props

  if (programKey > 0) {
    try {
      const resultingPayment = await startPayment({
        body: {
          _for: {
            type: PaymentStartRequestBodyForTypeEnum.Licensing,
            programKey,
          },
          amount: !!amountOfPayment
            ? getAmountFromLocaleCurrency(amountOfPayment)
            : formatBigFixedTwoDecimals(total),
          method: PaymentMethodKey.Ach,
        },
        actorKey,
      })
      setPaymentInformation(resultingPayment)
      setErrorMessage('')
      setStep(PaymentModalSteps.EnterBillingAddress)
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown',
        message: (e as unknown as Error).message ?? defaultErrorMessage,
      }
      setErrorMessage(errorObject.message)
    } finally {
      setSubmissionInProgress(false)
    }
  }
}

export const handleEnterBillingAddress = async (
  props: HandleEnterBillingAddressProps,
  setStepToValidatePaymentBillingAddress: (
    step?: PaymentBillingAddressValidationSteps
  ) => void,
  setBillingAddressInformation: (info: {
    fullName: string
    address: Address
  }) => void
): Promise<void> => {
  const {
    stepToValidatePaymentBillingAddress,
    validateAddress,
    geocodeAddress,
    updateBillingPaymentAddress,
    selectedValidAddress,
    billingAddressInformation,
    initialValueForPaymentBillingAddress,
  } = props

  if (
    stepToValidatePaymentBillingAddress === initialValueForPaymentBillingAddress
  ) {
    const isValidAddress = await validateAddress()
    if (!isValidAddress) {
      return
    }
  }

  if (
    stepToValidatePaymentBillingAddress ===
      PaymentBillingAddressValidationSteps.UnverifiedAddress &&
    !geocodeAddress
  ) {
    return
  }

  if (
    stepToValidatePaymentBillingAddress ===
    PaymentBillingAddressValidationSteps.SelectValidAddress
  ) {
    setStepToValidatePaymentBillingAddress(
      PaymentBillingAddressValidationSteps.ValidatingAddress
    )
    const selectedAddress = {
      ...billingAddressInformation,
      address: {
        ...JSON.parse(selectedValidAddress),
        countryCode: 'US',
        streetAddress2: billingAddressInformation.address.streetAddress2 ?? '',
      },
    }
    setBillingAddressInformation(selectedAddress)

    await updateBillingPaymentAddress(selectedAddress.address)
  }

  if (
    stepToValidatePaymentBillingAddress !==
    PaymentBillingAddressValidationSteps.SelectValidAddress
  ) {
    await updateBillingPaymentAddress()
  }
}

export const handleEnterPaymentInformation = async (
  props: HandleEnterPaymentInformationProps,
  setBankAccountMatch: (match: { input: boolean }) => void,
  setValidRoutingNumber: (valid: { input: boolean }) => void,
  setSubmissionInProgress: (inProgress: boolean) => void,
  setErrorMessage: (msg: string) => void,
  setTaxes: (taxes: string) => void,
  setPaymentInformation: (payment: PaymentResponse) => void,
  setStep: (step: PaymentModalSteps) => void,
  setErrorProcessingPayment: (processing: boolean) => void,
  setAllowRestart: (restart: boolean) => void,
  addLoadingIds: (ids: string[]) => void,
  loadingIdsToAddOnComplete?: string[]
): Promise<void> => {
  const {
    bankPaymentInformation,
    bankAccountMatch,
    validRoutingNumber,
    paymentInformation,
    isBankSelected,
    bankAccountsDoesNotMatch,
    defaultMessageError,
    defaultAchErrorMessage,
    contactCustomerSupportMessage,
  } = props

  const doesTheBankAccountNumberMatchTheConfirmBankAccountNumber =
    bankPaymentInformation.bankAccountNumber ===
    bankPaymentInformation.confirmBankAccountNumber

  setBankAccountMatch({
    ...bankAccountMatch,
    input: doesTheBankAccountNumberMatchTheConfirmBankAccountNumber,
  })

  if (!doesTheBankAccountNumberMatchTheConfirmBankAccountNumber) {
    setSubmissionInProgress(false)
    setErrorMessage(bankAccountsDoesNotMatch)
    return
  }

  let isValidRoutingNumber
  try {
    if (doesTheBankAccountNumberMatchTheConfirmBankAccountNumber) {
      const validRoutingNumberResult =
        await paymentsApi.validateAchRoutingNumber({
          routingNumber: bankPaymentInformation.routingNumber,
        })
      isValidRoutingNumber = validRoutingNumberResult.validRoutingNumber
    }
  } catch (e) {
    const errorObject = (await extractedErrorObject(e)) ?? {
      code: 'Unknown',
      message: (e as unknown as Error).message ?? '',
    }
    setErrorMessage(errorObject.message)
  }

  if (
    !isValidRoutingNumber &&
    doesTheBankAccountNumberMatchTheConfirmBankAccountNumber
  ) {
    setSubmissionInProgress(false)
    setErrorMessage(defaultMessageError)
    setValidRoutingNumber({
      ...validRoutingNumber,
      input: isValidRoutingNumber as boolean,
    })
  }

  if (isValidRoutingNumber) {
    setValidRoutingNumber({
      ...validRoutingNumber,
      input: isValidRoutingNumber as boolean,
    })
  }

  if (!!paymentInformation) {
    if (
      isBankSelected &&
      doesTheBankAccountNumberMatchTheConfirmBankAccountNumber &&
      isValidRoutingNumber
    ) {
      try {
        const result = await payByAch({
          paymentKey: paymentInformation.payment.paymentKey,
          body: {
            routing: bankPaymentInformation.routingNumber,
            account: bankPaymentInformation.bankAccountNumber,
          },
        })

        if (result instanceof Error) {
          throw result
        } else {
          const achSuccess =
            PaymentStatusKey.Paid === result.payment.paymentStatusKey ||
            PaymentStatusKey.Pend === result.payment.paymentStatusKey

          if (achSuccess) {
            setTaxes(result.payment.tax.amount)
            setPaymentInformation(result)
            setErrorMessage('')
            setStep(PaymentModalSteps.PaymentComplete)

            if (
              loadingIdsToAddOnComplete &&
              loadingIdsToAddOnComplete.length > 0
            ) {
              addLoadingIds(loadingIdsToAddOnComplete)
            }
          } else {
            setErrorMessage(defaultAchErrorMessage)
            setErrorProcessingPayment(true)
            setAllowRestart(false)
          }
        }
      } catch (e) {
        const errorObject = (await extractedErrorObject(e)) ?? {
          code: 'Unknown',
          message: defaultAchErrorMessage,
        }
        const isBlockingError = errorObject.code === 'ThirdPartyFailure'
        setErrorMessage(
          isBlockingError ? contactCustomerSupportMessage : errorObject.message
        )
        setErrorProcessingPayment(true)
        setAllowRestart(false)
      } finally {
        setSubmissionInProgress(false)
      }
    }
  }
}

export const handleLCDStep = async (
  step: LcdAmountSelection,
  programs: Array<{
    programKey: number
    programName: string
    amountDue: CashAmount
    amountOwed: CashAmount
  }>,
  totalAmountDue: CashAmount,
  totalAmountOwed: CashAmount,
  actorKey: number | undefined,
  setPaymentInformation: (payment: PaymentResponse) => void,
  setStep: (step: PaymentModalSteps) => void,
  setErrorMessage: (msg: string) => void,
  setSubmissionInProgress: (inProgress: boolean) => void
): Promise<void> => {
  if (step === 'enterDifferentAmount') {
    setErrorMessage('')
    setStep(PaymentModalSteps.LCDAmountStep)
    setSubmissionInProgress(false)
  } else if (programs.length > 0) {
    try {
      const filteredPrograms = programs.filter((program) => {
        const amount =
          step === 'payTotalAmountDue'
            ? program.amountDue.amount
            : program.amountOwed.amount
        return !convertTwoDecimalStringToBig(amount).eq(0)
      })

      if (filteredPrograms.length === 0) {
        setSubmissionInProgress(false)
        return
      }

      const licensingArray = filteredPrograms.map((program) => ({
        programKey: program.programKey,
      }))

      const resultingPayment = await startPayment({
        body: {
          _for: {
            type: PaymentStartRequestBodyForTypeEnum.Licensing,
            licensing: licensingArray,
          },
          amount:
            step === 'payTotalAmountDue'
              ? totalAmountDue.amount
              : totalAmountOwed.amount,
          method: PaymentMethodKey.Ach,
        },
        actorKey,
      })

      setPaymentInformation(resultingPayment)
      setErrorMessage('')
      setStep(PaymentModalSteps.EnterBillingAddress)
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown',
        message: (e as unknown as Error).message ?? 'Failed to create payment',
      }
      setErrorMessage(errorObject.message)
    } finally {
      setSubmissionInProgress(false)
    }
  }
}

export const handleLCDAmountStep = async (
  selections: LcdProgramSelection[],
  totalAmount: Big,
  actorKey: number | undefined,
  setPaymentInformation: (payment: PaymentResponse) => void,
  setStep: (step: PaymentModalSteps) => void,
  setErrorMessage: (msg: string) => void,
  setSubmissionInProgress: (inProgress: boolean) => void
): Promise<void> => {
  if (selections.length === 0) {
    setErrorMessage('')
    setStep(PaymentModalSteps.EnterBillingAddress)
    setSubmissionInProgress(false)
    return
  }

  try {
    const licensingArray = selections
      .map((selection) => ({
        programKey: selection.programKey,
        amount: selection.amount,
      }))
      .filter((selection) =>
        convertTwoDecimalStringToBig(selection.amount).gt(0)
      )

    const resultingPayment = await startPayment({
      body: {
        _for: {
          type: PaymentStartRequestBodyForTypeEnum.Licensing,
          licensing: licensingArray,
        },
        amount: formatBigFixedTwoDecimals(totalAmount),
        method: PaymentMethodKey.Ach,
      },
      actorKey,
    })

    setPaymentInformation(resultingPayment)
    setErrorMessage('')
    setStep(PaymentModalSteps.EnterBillingAddress)
  } catch (e) {
    const errorObject = (await extractedErrorObject(e)) ?? {
      code: 'Unknown',
      message: (e as unknown as Error).message ?? 'Failed to create payment',
    }
    setErrorMessage(errorObject.message)
  } finally {
    setSubmissionInProgress(false)
  }
}
